import React, { useMemo } from 'react';

import { ExpandableJobCard } from '@/components/features/ExpandableJobCard';
import { JobIcon } from '@/constant';
import { JOB_STATUS } from '@/types/jobs';
import { RelatedWorks, SearchOutcomeHit } from '@/types/search';
import { getJobStatusCategoryColors, getJobStatusLabel } from '@/utils/job';
import {
  getJobCardDateLabel,
  getJobCardEngineerLabel,
} from '@/utils/jobCardStatusLine';
import {
  hasExpandableOutcomeContent,
  parseJobStepsBreakdown,
  parseJobTechnicalDetails,
} from '@/utils/searchOutcomes';

import { OutcomeExpandedDetails } from './OutcomeExpandedDetails';
import { RelatedWorksPanel } from './related';

interface OutcomeResultCardProps {
  hit: SearchOutcomeHit;
  onSelect: (hit: SearchOutcomeHit) => void;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  relatedWorks?: RelatedWorks;
  isRelatedLoading?: boolean;
  isRelatedError?: boolean;
  outcomeHits?: SearchOutcomeHit[];
  onOpenRelatedJob?: (jobId: number) => void;
  showSelection?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (hit: SearchOutcomeHit) => void;
}

const isJobStatus = (value: string): value is JOB_STATUS => {
  return Object.values(JOB_STATUS).includes(value as JOB_STATUS);
};

export const OutcomeResultCard: React.FC<OutcomeResultCardProps> = ({
  hit,
  onSelect,
  isExpanded = false,
  onExpandedChange,
  relatedWorks,
  isRelatedLoading = false,
  isRelatedError = false,
  outcomeHits,
  onOpenRelatedJob,
  showSelection = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const technicalDetails = useMemo(
    () => parseJobTechnicalDetails(hit.jobTechnicalDetails),
    [hit.jobTechnicalDetails]
  );

  const stepsBreakdown = useMemo(
    () => parseJobStepsBreakdown(hit.jobStepsBreakdown),
    [hit.jobStepsBreakdown]
  );

  const hasPhase2Details = useMemo(
    () =>
      hasExpandableOutcomeContent({
        technicalDetails,
        stepsBreakdown,
      }),
    [technicalDetails, stepsBreakdown]
  );

  const statusLabel = useMemo(() => {
    if (hit.status && isJobStatus(hit.status)) {
      return getJobStatusLabel(hit.status);
    }
    if (hit.status) return hit.status;
    if (hit.result) return hit.result;
    return null;
  }, [hit.status, hit.result]);

  const statusColor = useMemo(() => {
    if (hit.status && isJobStatus(hit.status)) {
      return getJobStatusCategoryColors(hit.status).color;
    }
    return 'tags.blue.font';
  }, [hit.status]);

  const matchPercent = useMemo(() => {
    if (typeof hit.score !== 'number' || Number.isNaN(hit.score)) return null;
    return Math.round(hit.score * 100);
  }, [hit.score]);

  const sublabel = useMemo(() => {
    const parts = [hit.contactName, hit.typeName].filter(
      (part): part is string => Boolean(part && part.trim())
    );
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [hit.contactName, hit.typeName]);

  const dateLabel = useMemo(
    () =>
      getJobCardDateLabel({
        actualEndAt: hit.actualEndAt,
        actualStartAt: hit.actualStartAt,
        plannedStartAt: hit.plannedStartAt,
      }),
    [hit.actualEndAt, hit.actualStartAt, hit.plannedStartAt]
  );

  const engineerName = useMemo(
    () => getJobCardEngineerLabel(hit.resourceName),
    [hit.resourceName]
  );

  return (
    <ExpandableJobCard
      title={hit.jobRef}
      sublabel={sublabel}
      statusLabel={statusLabel}
      statusColor={statusColor}
      dateLabel={dateLabel}
      engineerName={engineerName}
      summary={hit.jobNaturalSummary}
      matchPercent={matchPercent}
      icon={<JobIcon size={16} />}
      isExpanded={isExpanded}
      onToggleExpand={() => onExpandedChange?.(!isExpanded)}
      onOpenJob={() => onSelect(hit)}
      openJobAriaLabel={`Open job ${hit.jobRef}`}
      showSelection={showSelection}
      isSelected={isSelected}
      onToggleSelect={() => onToggleSelect?.(hit)}
      selectAriaLabel={`Select job ${hit.jobRef}`}
      deselectAriaLabel={`Deselect job ${hit.jobRef}`}
    >
      {hasPhase2Details ? (
        <OutcomeExpandedDetails
          technicalDetails={technicalDetails}
          stepsBreakdown={stepsBreakdown}
          summary={hit.jobNaturalSummary}
          fullText={hit.text}
        />
      ) : null}
      <RelatedWorksPanel
        hit={hit}
        relatedWorks={relatedWorks}
        isLoading={isRelatedLoading}
        isError={isRelatedError}
        outcomeHits={outcomeHits}
        onOpenJob={onOpenRelatedJob}
      />
    </ExpandableJobCard>
  );
};
